import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Phone } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBibHVlcHJpbnRzfGVufDB8fHx8MTc4MzgxMjU2Nnww&ixlib=rb-4.1.0&q=85";

// Text is split into words; each word rides its own mask.
function MaskedLine({ text, delay = 0, size = "hero" }) {
  const words = text.split(" ");
  const cls =
    size === "hero"
      ? "font-oswald uppercase tracking-[-0.03em] text-white text-[16vw] md:text-[13vw] lg:text-[11.5vw] leading-[0.92]"
      : "font-editorial italic text-white/85 text-2xl md:text-4xl leading-tight";

  return (
    <div className={`flex flex-wrap gap-x-[0.25em] gap-y-0 ${cls}`}>
      {words.map((w, i) => (
        <span key={i} className="word-mask">
          <motion.span
            className="inline-block"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{
              duration: 1.1,
              ease: [0.76, 0, 0.24, 1],
              delay: delay + i * 0.08,
            }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </div>
  );
}

export default function Hero({ start }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [0.55, 0.15]);

  useEffect(() => {
    // ensure top-most anchor exists
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-black"
    >
      {/* Parallax bg */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 -z-10 will-change-transform"
      >
        <img
          src={HERO_IMG}
          alt="Architectural blueprint background"
          className="h-full w-full object-cover img-grayscale"
          style={{ filter: "grayscale(1) contrast(1.05) brightness(0.55)" }}
        />
      </motion.div>
      <motion.div
        style={{ opacity }}
        className="absolute inset-0 -z-10 bg-gradient-to-b from-black/60 via-black/40 to-black"
      />
      <div className="pointer-events-none absolute inset-0 -z-10 grain" />

      {/* Top meta rail */}
      <div className="pointer-events-none absolute inset-x-0 top-24 z-10 mx-auto flex max-w-[1600px] items-center justify-between px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={start ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="eyebrow"
        >
          <span className="text-white/60">◍</span>{" "}
          <span className="text-white/80">
            JLP CONSTRUCT & REPAIR LLC — PENNSYLVANIA
          </span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={start ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="eyebrow hidden md:block"
        >
          N 40°36′ · W 75°28′
        </motion.div>
      </div>

      {/* Headline block */}
      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1600px] flex-col justify-end px-6 pb-16 pt-40 md:px-10 md:pb-24 md:pt-52">
        <div className="max-w-[1400px]">
          <MaskedLine text="Fixing Homes." delay={start ? 0.25 : 99} />
          <div className="mt-1 md:mt-2">
            <MaskedLine text="Building Dreams." delay={start ? 0.55 : 99} />
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={start ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 1.6, ease: [0.2, 0.8, 0.2, 1] }}
          className="mt-10 grid gap-8 md:mt-14 md:grid-cols-[1.1fr_1fr] md:items-end"
        >
          <p className="max-w-xl font-editorial text-lg italic text-white/70 md:text-xl">
            From a squeaking hinge to a fifty-thousand-square-foot commercial
            build — one crew, one standard, one phone number that answers day or
            night.
          </p>

          <div className="flex flex-wrap items-center gap-3 md:justify-end">
            <a
              data-testid="hero-call-cta"
              href="tel:6103497313"
              className="btn-primary"
            >
              <Phone size={16} />
              Call 24/7 · 610-349-7313
            </a>
            <a
              data-testid="hero-quote-cta"
              href="#estimate"
              className="btn-ghost"
            >
              Request a Free Estimate
              <ArrowDownRight size={16} />
            </a>
          </div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={start ? { opacity: 1 } : {}}
        transition={{ delay: 2.2, duration: 0.8 }}
        className="pointer-events-none absolute bottom-6 left-6 z-10 flex items-center gap-3 md:left-10"
      >
        <span className="eyebrow">Scroll</span>
        <span className="block h-8 w-px bg-white/40" />
      </motion.div>
    </section>
  );
}
